/**
 * DoseSpot Patient Detail Drawer
 *
 * Right-side drawer for viewing and editing a DoseSpot patient's demographics.
 * DoseSpot PUT requires the full patient object, so we load it first and let
 * admins edit fields inline.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  useGetDosespotPatientQuery,
  useUpdateDosespotPatientMutation,
} from '../store/api';
import type { DosespotPatientData } from '../store/api';

interface DosespotPatientDrawerProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  dosespotPatientId: string | null;
}

const DRAWER_WIDTH = 450;

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Unknown', label: 'Unknown' },
];

const PHONE_TYPE_OPTIONS = [
  { value: 'Undefined', label: 'Undefined' },
  { value: 'Home', label: 'Home' },
  { value: 'Work', label: 'Work' },
  { value: 'Mobile', label: 'Mobile' },
];

export function DosespotPatientDrawer({
  open,
  onClose,
  organizationId,
  dosespotPatientId,
}: DosespotPatientDrawerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<DosespotPatientData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useGetDosespotPatientQuery(
    { organizationId, dosespotPatientId: dosespotPatientId || '' },
    { skip: !dosespotPatientId || !open }
  );

  const [updatePatient, { isLoading: isSaving }] = useUpdateDosespotPatientMutation();

  // Populate form when data loads
  useEffect(() => {
    if (data?.Item) {
      setForm({ ...data.Item });
      setError(null);
    }
  }, [data]);

  // Reset when drawer closes
  useEffect(() => {
    if (!open) {
      setForm(null);
      setError(null);
    }
  }, [open]);

  const handleChange = useCallback(
    (field: keyof DosespotPatientData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm((prev) => (prev ? { ...prev, [field]: e.target.value } : prev));
        if (error) setError(null);
      },
    [error]
  );

  const handleNumberChange = useCallback(
    (field: keyof DosespotPatientData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const parsed = e.target.value === '' ? undefined : Number(e.target.value);
        setForm((prev) => (prev ? { ...prev, [field]: parsed } : prev));
        if (error) setError(null);
      },
    [error]
  );

  const handleSwitchChange = useCallback(
    (field: keyof DosespotPatientData) => (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setForm((prev) => (prev ? { ...prev, [field]: checked } : prev));
    },
    []
  );

  const handleSave = async () => {
    if (!form || !dosespotPatientId) return;

    // Basic validation
    if (!form.FirstName?.trim()) {
      setError('First name is required');
      return;
    }
    if (!form.LastName?.trim()) {
      setError('Last name is required');
      return;
    }
    if (!form.DateOfBirth?.trim()) {
      setError('Date of birth is required');
      return;
    }
    if (!form.Address1?.trim()) {
      setError('Address is required');
      return;
    }
    if (!form.City?.trim()) {
      setError('City is required');
      return;
    }
    if (!form.State?.trim()) {
      setError('State is required');
      return;
    }
    if (!form.ZipCode?.trim()) {
      setError('ZIP code is required');
      return;
    }
    if (!form.PrimaryPhone?.trim()) {
      setError('Primary phone is required');
      return;
    }

    try {
      await updatePatient({
        organizationId,
        dosespotPatientId,
        body: form,
      }).unwrap();
      enqueueSnackbar('Patient updated successfully', { variant: 'success' });
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { error?: string } }).data?.error || 'Update failed')
          : 'Failed to update patient';
      setError(message);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose} slotProps={{ paper: { sx: { width: DRAWER_WIDTH } } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <div>
          <Typography variant="h6">
            {form ? `${form.FirstName} ${form.LastName}` : 'Patient Details'}
          </Typography>
          {dosespotPatientId && (
            <Typography variant="caption" color="text.secondary">
              DoseSpot ID: {dosespotPatientId}
            </Typography>
          )}
        </div>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {form && !isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Name */}
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Prefix"
                size="small"
                value={form.Prefix || ''}
                onChange={handleChange('Prefix')}
                sx={{ width: 80 }}
              />
              <TextField
                label="First Name"
                size="small"
                value={form.FirstName || ''}
                onChange={handleChange('FirstName')}
                required
                sx={{ flex: 1 }}
               slotProps={{ htmlInput: { maxLength: 35 } }}/>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Middle Name"
                size="small"
                value={form.MiddleName || ''}
                onChange={handleChange('MiddleName')}
                sx={{ flex: 1 }}
               slotProps={{ htmlInput: { maxLength: 35 } }}/>
              <TextField
                label="Last Name"
                size="small"
                value={form.LastName || ''}
                onChange={handleChange('LastName')}
                required
                sx={{ flex: 1 }}
               slotProps={{ htmlInput: { maxLength: 35 } }}/>
              <TextField
                label="Suffix"
                size="small"
                value={form.Suffix || ''}
                onChange={handleChange('Suffix')}
                sx={{ width: 80 }}
              />
            </Box>

            <Divider />

            {/* Demographics */}
            <Typography variant="subtitle2" color="text.secondary">
              Demographics
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Date of Birth"
                size="small"
                type="date"
                value={form.DateOfBirth ? form.DateOfBirth.split('T')[0] : ''}
                onChange={handleChange('DateOfBirth')}
                required
                sx={{ flex: 1 }}
               slotProps={{ inputLabel: { shrink: true } }}/>
              <TextField
                label="Gender"
                size="small"
                select
                value={form.Gender || ''}
                onChange={handleChange('Gender')}
                required
                sx={{ flex: 1 }}
              >
                {GENDER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label="Email"
              size="small"
              value={form.Email || ''}
              onChange={handleChange('Email')}
             slotProps={{ htmlInput: { maxLength: 80 } }}/>

            <Divider />

            {/* Address */}
            <Typography variant="subtitle2" color="text.secondary">
              Address
            </Typography>
            <TextField
              label="Address Line 1"
              size="small"
              value={form.Address1 || ''}
              onChange={handleChange('Address1')}
              required
             slotProps={{ htmlInput: { maxLength: 35 } }}/>
            <TextField
              label="Address Line 2"
              size="small"
              value={form.Address2 || ''}
              onChange={handleChange('Address2')}
             slotProps={{ htmlInput: { maxLength: 35 } }}/>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="City"
                size="small"
                value={form.City || ''}
                onChange={handleChange('City')}
                required
                sx={{ flex: 2 }}
               slotProps={{ htmlInput: { maxLength: 35 } }}/>
              <TextField
                label="State"
                size="small"
                value={form.State || ''}
                onChange={handleChange('State')}
                required
                sx={{ flex: 1 }}
                placeholder="MA"
               slotProps={{ htmlInput: { maxLength: 10 } }}/>
              <TextField
                label="ZIP"
                size="small"
                value={form.ZipCode || ''}
                onChange={handleChange('ZipCode')}
                required
                sx={{ flex: 1 }}
               slotProps={{ htmlInput: { maxLength: 10 } }}/>
            </Box>

            <Divider />

            {/* Phone */}
            <Typography variant="subtitle2" color="text.secondary">
              Phone
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Primary Phone"
                size="small"
                value={form.PrimaryPhone || ''}
                onChange={handleChange('PrimaryPhone')}
                required
                sx={{ flex: 2 }}
               slotProps={{ htmlInput: { maxLength: 25 } }}/>
              <TextField
                label="Type"
                size="small"
                select
                value={form.PrimaryPhoneType || 'Undefined'}
                onChange={handleChange('PrimaryPhoneType')}
                sx={{ flex: 1 }}
              >
                {PHONE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Divider />

            {/* Weight / Height */}
            <Typography variant="subtitle2" color="text.secondary">
              Physical
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Weight"
                size="small"
                type="number"
                value={form.Weight ?? ''}
                onChange={handleNumberChange('Weight')}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Height"
                size="small"
                type="number"
                value={form.Height ?? ''}
                onChange={handleNumberChange('Height')}
                sx={{ flex: 1 }}
              />
            </Box>

            <Divider />

            {/* Status */}
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.Active ?? true}
                    onChange={handleSwitchChange('Active')}
                    size="small"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.IsHospice ?? false}
                    onChange={handleSwitchChange('IsHospice')}
                    size="small"
                  />
                }
                label="Hospice"
              />
            </Box>

            <TextField
              label="EMR Record Number"
              size="small"
              value={form.NonDoseSpotMedicalRecordNumber || ''}
              onChange={handleChange('NonDoseSpotMedicalRecordNumber')}
              helperText="External medical record identifier"
             slotProps={{ htmlInput: { maxLength: 35 } }}/>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {form && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
